import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Minus, Plus } from "lucide-react";
import { MaskedLine, FadeUp, Overline } from "../components/Reveal";
import { CountUp } from "../components/CountUp";
import { Marquee } from "../components/Marquee";
import { useEnquiry } from "../context/EnquiryContext";
import {
  ABOUT_COPY_1,
  ABOUT_COPY_2,
  ABOUT_COPY_MANUFACTURER,
  BRANDS_ASSOCIATIONS,
  CATEGORIES,
  IMAGES,
  INDUSTRIES,
  LEADERSHIP,
  MISSION,
  PHONE_1,
  PHONE_1_LINK,
  PHONE_2,
  PHONE_2_LINK,
  VISION,
  WHATSAPP_LINK,
  WHY_POINTS,
} from "../data/content";

const Hero = () => {
  const { openEnquiry } = useEnquiry();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "10%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative pt-28 sm:pt-36 lg:pt-40 border-b border-ink/15 overflow-hidden">
      <div className="px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8">
        <div className="lg:col-span-7 pb-12 lg:pb-20">
          <FadeUp immediate delay={0.1} y={12}>
            <Overline className="mb-6 sm:mb-8">Reliable | Genuine | On-Time</Overline>
          </FadeUp>
          <h1 className="font-display font-black uppercase tracking-[-0.02em] leading-[0.9] text-[12vw] sm:text-[12vw] lg:text-[5.6vw] xl:text-[6vw]">
            <MaskedLine immediate delay={0.15}>Hardware.</MaskedLine>
            <MaskedLine immediate delay={0.3}>Industrial.</MaskedLine>
            <MaskedLine immediate delay={0.45}>
              Dependable<span className="text-accent" style={{ WebkitTextStroke: "2px #0A0A0A" }}>.</span>
            </MaskedLine>
          </h1>
          <FadeUp delay={0.65} className="mt-8 sm:mt-10 max-w-xl">
            <p className="text-base sm:text-lg text-ink/70 leading-relaxed">
              Quality hardware, tools and industrial supplies for businesses, workshops,
              industries and professionals.
            </p>
          </FadeUp>
          <FadeUp delay={0.8} className="mt-8 sm:mt-10 flex flex-wrap gap-4">
            <button
              data-testid="hero-cta-send-requirement"
              onClick={() => openEnquiry("")}
              className="inline-flex items-center gap-2 bg-accent text-ink font-mono text-[11px] sm:text-xs tracking-[0.2em] uppercase px-7 py-4 border border-ink hover:bg-ink hover:text-paper transition-colors duration-300"
            >
              Send Your Requirement <ArrowRight size={14} />
            </button>
            <Link
              to="/products"
              data-testid="hero-cta-explore-products"
              className="inline-flex items-center gap-2 bg-transparent text-ink font-mono text-[11px] sm:text-xs tracking-[0.2em] uppercase px-7 py-4 border border-ink/30 hover:border-ink transition-colors duration-300"
            >
              Explore Products <ArrowRight size={14} />
            </Link>
          </FadeUp>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative h-[42vh] lg:h-[calc(100%-2rem)] min-h-[320px] overflow-hidden border-l border-ink/15"
          >
            <motion.img
              src={IMAGES.hero}
              alt="Precision industrial bolts and hardware"
              style={{ y: imgY }}
              className="absolute inset-0 h-[115%] w-full object-cover grayscale contrast-125"
            />
            <div className="absolute bottom-0 left-0 bg-ink text-paper px-5 py-4">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-paper/60">
                25+ Years of Supply
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Metrics = () => (
  <section data-testid="trust-metrics-section" className="border-b border-ink/15">
    <div className="grid grid-cols-1 md:grid-cols-3">
      {[
        { value: 25, suffix: "+", label: "Years of Experience" },
        { value: 50, suffix: "+", label: "Trusted Brands" },
        { value: 100, suffix: "K+", label: "Satisfied Customers" },
      ].map((m, i) => (
        <div
          key={m.label}
          data-testid={`metric-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={`px-6 sm:px-10 lg:px-12 py-12 sm:py-16 border-ink/15 ${
            i > 0 ? "border-t md:border-t-0 md:border-l" : ""
          }`}
        >
          <FadeUp delay={i * 0.1}>
            <p className="font-display font-black tracking-tighter leading-none text-7xl sm:text-8xl">
              <CountUp to={m.value} suffix={m.suffix} />
            </p>
            <div className="mt-6 h-px w-10 bg-accent" />
            <p className="mt-4 font-mono text-[11px] tracking-[0.25em] uppercase text-ink/50">
              {m.label}
            </p>
          </FadeUp>
        </div>
      ))}
    </div>
  </section>
);

const AboutPreview = () => (
  <section data-testid="about-preview-section" className="border-b border-ink/15">
    <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-7">
        <FadeUp>
          <Overline className="mb-6">About Jagdamba</Overline>
        </FadeUp>
        <h2 className="font-display font-black uppercase tracking-tighter leading-[0.92] text-4xl sm:text-6xl lg:text-7xl">
          <MaskedLine>Built on Trust.</MaskedLine>
          <MaskedLine delay={0.12}>Delivered with</MaskedLine>
          <MaskedLine delay={0.24}>Precision.</MaskedLine>
        </h2>
        <FadeUp delay={0.2} className="mt-10 max-w-xl space-y-5">
          <p className="text-base sm:text-lg text-ink/75 leading-relaxed">{ABOUT_COPY_1}</p>
          <p className="text-base sm:text-lg text-ink/75 leading-relaxed border-l-2 border-accent pl-5">
            {ABOUT_COPY_MANUFACTURER}
          </p>
          <p className="text-base sm:text-lg text-ink/75 leading-relaxed">{ABOUT_COPY_2}</p>
        </FadeUp>
        <FadeUp delay={0.3} className="mt-10">
          <Link
            to="/about"
            data-testid="about-preview-cta"
            className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase link-underline"
          >
            About Jagdamba <ArrowRight size={14} />
          </Link>
        </FadeUp>
      </div>
      <div className="lg:col-span-5">
        <FadeUp delay={0.15} className="h-full">
          <div className="h-full min-h-[320px] overflow-hidden border border-ink/15">
            <img
              src={IMAGES.fittings}
              alt="Industrial pipe fittings and components"
              className="h-full w-full object-cover grayscale contrast-125 hover:scale-[1.03] transition-transform duration-[1.2s] ease-out"
              loading="lazy"
            />
          </div>
        </FadeUp>
      </div>
    </div>
  </section>
);

const ProductRange = () => {
  const [openIdx, setOpenIdx] = useState(0);
  const { openEnquiry } = useEnquiry();

  return (
    <section data-testid="product-range-section" className="border-b border-ink/15">
      <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-24 lg:py-32">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <FadeUp>
              <Overline className="mb-6">Product Range</Overline>
            </FadeUp>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.92] text-4xl sm:text-6xl lg:text-7xl">
              <MaskedLine>What We</MaskedLine>
              <MaskedLine delay={0.12}>Supply</MaskedLine>
            </h2>
          </div>
          <FadeUp delay={0.2} className="max-w-sm">
            <p className="text-sm sm:text-base text-ink/60 leading-relaxed">
              A broad range of hardware, tools and industrial supplies for professional
              requirements.
            </p>
          </FadeUp>
        </div>

        <div className="border-t border-ink/15">
          {CATEGORIES.map((cat, i) => {
            const isOpen = openIdx === i;
            return (
              <div key={cat.id} className="border-b border-ink/15" data-testid={`category-row-${cat.id}`}>
                <button
                  data-testid={`category-toggle-${cat.id}`}
                  onClick={() => setOpenIdx(isOpen ? -1 : i)}
                  className="w-full flex items-center gap-5 sm:gap-10 py-6 sm:py-8 text-left group"
                  aria-expanded={isOpen}
                >
                  <span className="font-mono text-xs text-ink/40 group-hover:text-accent transition-colors">
                    {cat.id}
                  </span>
                  <span
                    className={`flex-1 font-display font-extrabold uppercase tracking-tight text-2xl sm:text-4xl lg:text-5xl transition-colors ${
                      isOpen ? "text-ink" : "text-ink/70 group-hover:text-ink"
                    }`}
                  >
                    {cat.name}
                  </span>
                  <span className="text-ink/40 group-hover:text-ink transition-colors">
                    {isOpen ? <Minus size={20} strokeWidth={1.5} /> : <Plus size={20} strokeWidth={1.5} />}
                  </span>
                </button>
                <motion.div
                  initial={false}
                  animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden"
                >
                  <div className="pb-8 pl-10 sm:pl-24 pr-4">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                      <ul className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                        {cat.items.map((item) => (
                          <li key={item} className="border-b border-ink/10">
                            <button
                              data-testid={`enquire-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                              onClick={() => openEnquiry(item)}
                              className="w-full flex items-center justify-between gap-4 py-3 text-left group/item"
                            >
                              <span className="text-sm sm:text-base text-ink/80 group-hover/item:text-ink">
                                {item}
                              </span>
                              <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-ink/40 group-hover/item:text-ink flex items-center gap-1 transition-colors">
                                Enquire <ArrowUpRight size={12} />
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                      {cat.img && (
                        <div className="lg:col-span-4">
                          <div className="overflow-hidden border border-ink/15 aspect-[4/3]">
                            <img
                              src={cat.img}
                              alt={`${cat.name} supplied by Jagdamba Enterprises`}
                              className="h-full w-full object-cover grayscale contrast-125"
                              loading="lazy"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    {cat.note && (
                      <p className="mt-5 font-mono text-[11px] tracking-[0.1em] uppercase text-ink/45">
                        {cat.note}
                      </p>
                    )}
                  </div>
                </motion.div>
              </div>
            );
          })}
        </div>

        <FadeUp className="mt-10">
          <Link
            to="/products"
            data-testid="product-range-view-all"
            className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase link-underline"
          >
            Search the Full Range <ArrowRight size={14} />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
};

const WhyJagdamba = () => (
  <section data-testid="why-jagdamba-section" className="border-b border-ink/15 bg-white">
    <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-24 lg:py-32">
      <FadeUp>
        <Overline className="mb-6">Why Jagdamba?</Overline>
      </FadeUp>
      <div className="border-t border-ink/15">
        {WHY_POINTS.map((point, i) => (
          <FadeUp key={point} delay={i * 0.05}>
            <div className="flex items-baseline gap-6 sm:gap-12 py-6 sm:py-8 border-b border-ink/15">
              <span className="font-mono text-xs text-accent" style={{ WebkitTextStroke: "0.5px #0A0A0A" }}>
                0{i + 1}
              </span>
              <h3 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-4xl lg:text-5xl">
                {point}
              </h3>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const VisionMission = () => (
  <section data-testid="vision-mission-section" className="border-b border-ink/15">
    <div className="grid grid-cols-1 lg:grid-cols-2">
      <div className="px-6 sm:px-10 lg:px-12 py-14 sm:py-20 border-b lg:border-b-0 lg:border-r border-ink/15">
        <FadeUp>
          <Overline className="mb-8">Our Vision</Overline>
          <p className="font-display font-bold tracking-tight leading-[1.15] text-2xl sm:text-3xl lg:text-4xl">
            {VISION}
          </p>
        </FadeUp>
      </div>
      <div className="px-6 sm:px-10 lg:px-12 py-14 sm:py-20">
        <FadeUp delay={0.1}>
          <Overline className="mb-8">Our Mission</Overline>
          <p className="font-display font-bold tracking-tight leading-[1.15] text-2xl sm:text-3xl lg:text-4xl">
            {MISSION}
          </p>
        </FadeUp>
      </div>
    </div>
  </section>
);

const Leadership = () => (
  <section data-testid="leadership-section" className="border-b border-ink/15 bg-white">
    <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-24 lg:py-32">
      <FadeUp>
        <Overline className="mb-6">Leadership</Overline>
      </FadeUp>
      <h2 className="font-display font-black uppercase tracking-tighter leading-[0.92] text-4xl sm:text-6xl lg:text-7xl mb-14 sm:mb-20">
        <MaskedLine>The People Behind</MaskedLine>
        <MaskedLine delay={0.12}>Jagdamba</MaskedLine>
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/15 border border-ink/15">
        {LEADERSHIP.map((p, i) => (
          <div key={p.name} className="bg-white" data-testid={`leader-${p.name.toLowerCase().replace(" ", "-")}`}>
            <FadeUp delay={i * 0.1}>
              <div className="aspect-[4/3] overflow-hidden border-b border-ink/15">
                <img
                  src={p.photo}
                  alt={`${p.name}, ${p.role} of Jagdamba Enterprises`}
                  className="h-full w-full object-cover object-top grayscale contrast-105 hover:grayscale-0 hover:scale-[1.02] transition-all duration-[1.2s] ease-out"
                  loading="lazy"
                />
              </div>
              <div className="px-8 sm:px-12 py-12 sm:py-16">
                <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-ink/45 mb-6">
                  {String(i + 1).padStart(2, "0")} — {p.role}
                </p>
                <h3 className="font-display font-black uppercase tracking-tighter leading-none text-4xl sm:text-6xl">
                  {p.name}
                </h3>
                <div className="mt-8 h-1 w-14 bg-accent" />
              </div>
            </FadeUp>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const IndustriesPreview = () => (
  <section data-testid="industries-preview-section" className="border-b border-ink/15">
    <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-24 lg:py-32">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
        <h2 className="font-display font-black uppercase tracking-tighter leading-[0.92] text-4xl sm:text-6xl lg:text-7xl">
          <MaskedLine>Who We</MaskedLine>
          <MaskedLine delay={0.12}>Serve</MaskedLine>
        </h2>
        <FadeUp delay={0.2}>
          <Link
            to="/industries"
            data-testid="industries-preview-link"
            className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase link-underline"
          >
            All Industries <ArrowRight size={14} />
          </Link>
        </FadeUp>
      </div>
      <div className="border-t border-ink/15">
        {INDUSTRIES.map((ind, i) => (
          <FadeUp key={ind.id} delay={i * 0.04}>
            <div className="grid grid-cols-12 gap-4 items-baseline py-6 sm:py-7 border-b border-ink/15 group">
              <span className="col-span-2 sm:col-span-1 font-mono text-xs text-ink/40">{ind.id}</span>
              <h3 className="col-span-10 sm:col-span-6 font-display font-extrabold uppercase tracking-tight text-xl sm:text-3xl text-ink/80 group-hover:text-ink transition-colors">
                {ind.title}
              </h3>
              <p className="hidden sm:block sm:col-span-5 text-sm text-ink/55 leading-relaxed">
                {ind.desc}
              </p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const BrandsPreview = () => (
  <section data-testid="brands-preview-section" className="border-b border-ink/15 bg-white">
    <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-24 lg:py-32">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
        <div>
          <FadeUp>
            <Overline className="mb-6">Brand Associations</Overline>
          </FadeUp>
          <h2 className="font-display font-black uppercase tracking-tighter leading-[0.92] text-4xl sm:text-6xl lg:text-7xl">
            <MaskedLine>Brands We</MaskedLine>
            <MaskedLine delay={0.12}>Work With</MaskedLine>
          </h2>
        </div>
        <FadeUp delay={0.2} className="max-w-sm">
          <p className="text-sm sm:text-base text-ink/60 leading-relaxed">
            Trusted products. Established associations.
          </p>
        </FadeUp>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-ink/15 border border-ink/15">
        {BRANDS_ASSOCIATIONS.slice(0, 8).map((brand) => (
          <div key={brand.name} className="bg-white px-6 py-10 sm:py-14 flex items-center justify-center">
            {brand.logo ? (
              <img
                src={brand.logo}
                alt={`${brand.name} logo`}
                className="max-h-10 sm:max-h-14 w-auto max-w-full object-contain mix-blend-multiply"
                loading="lazy"
              />
            ) : (
              <span className="font-display font-extrabold uppercase tracking-tight text-lg sm:text-2xl text-ink/70 text-center">
                {brand.name}
              </span>
            )}
          </div>
        ))}
      </div>
      <FadeUp className="mt-10">
        <Link
          to="/brands"
          data-testid="brands-preview-link"
          className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase link-underline"
        >
          View All Brands <ArrowRight size={14} />
        </Link>
      </FadeUp>
    </div>
  </section>
);

const Signature = () => (
  <section data-testid="signature-section" className="bg-ink text-paper border-b border-ink">
    <div className="px-5 sm:px-8 lg:px-12 py-24 sm:py-36 lg:py-44">
      <FadeUp>
        <Overline dark className="mb-10">The Jagdamba Promise</Overline>
      </FadeUp>
      <h2 className="font-display font-black uppercase tracking-tighter leading-[0.95] text-[11vw] sm:text-7xl lg:text-8xl">
        <MaskedLine>In Hardware,</MaskedLine>
        <MaskedLine delay={0.12}>Trust is Built</MaskedLine>
        <MaskedLine delay={0.24}>
          <span className="underline decoration-accent decoration-[6px] underline-offset-[12px]">
            Thread by Thread.
          </span>
        </MaskedLine>
      </h2>
      <FadeUp delay={0.35} className="mt-14 max-w-xl">
        <p className="text-base sm:text-lg text-paper/60 leading-relaxed">
          We deliver products that deliver performance, so you never have to worry about downtime.
        </p>
      </FadeUp>
    </div>
  </section>
);

const FinalCTA = () => {
  const { openEnquiry } = useEnquiry();
  return (
    <section data-testid="final-cta-section" className="bg-accent text-ink">
      <div className="px-5 sm:px-8 lg:px-12 py-24 sm:py-36 lg:py-44 min-h-[80vh] flex flex-col justify-center">
        <FadeUp>
          <Overline className="mb-8 !text-ink/60">Tell us what you need. We&apos;ll take it from there.</Overline>
        </FadeUp>
        <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-[11vw] sm:text-8xl lg:text-9xl">
          <MaskedLine>Have a</MaskedLine>
          <MaskedLine delay={0.12}>Requirement?</MaskedLine>
        </h2>
        <FadeUp delay={0.3} className="mt-12 flex flex-wrap items-center gap-4">
          <button
            data-testid="final-cta-send-requirement"
            onClick={() => openEnquiry("")}
            className="inline-flex items-center gap-2 bg-ink text-paper font-mono text-xs tracking-[0.2em] uppercase px-8 py-5 hover:bg-paper hover:text-ink transition-colors duration-300"
          >
            Send Your Requirement <ArrowRight size={14} />
          </button>
          <a
            data-testid="final-cta-whatsapp"
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-ink text-ink font-mono text-xs tracking-[0.2em] uppercase px-8 py-5 hover:bg-ink hover:text-paper transition-colors duration-300"
          >
            WhatsApp Us <ArrowUpRight size={14} />
          </a>
        </FadeUp>
        <FadeUp delay={0.4} className="mt-12 flex flex-wrap gap-x-10 gap-y-3">
          <a data-testid="final-cta-phone-1" href={PHONE_1_LINK} className="font-display font-bold text-xl sm:text-2xl tracking-tight link-underline">
            {PHONE_1}
          </a>
          <a data-testid="final-cta-phone-2" href={PHONE_2_LINK} className="font-display font-bold text-xl sm:text-2xl tracking-tight link-underline">
            {PHONE_2}
          </a>
        </FadeUp>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee
        items={["Reliable", "Genuine", "On-Time", "Hardware & Industrial Solutions", "Noida, India"]}
      />
      <Metrics />
      <AboutPreview />
      <ProductRange />
      <WhyJagdamba />
      <VisionMission />
      <Leadership />
      <IndustriesPreview />
      <BrandsPreview />
      <Signature />
      <FinalCTA />
    </main>
  );
}
